class MarijoaMegaMenu extends HTMLElement {
  constructor() {
    super();
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentFocusIn = this.onDocumentFocusIn.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onPromoTrigger = this.onPromoTrigger.bind(this);
    this.onPromoTriggerDelegated = this.onPromoTriggerDelegated.bind(this);
    this.onPromoSlotEnter = this.onPromoSlotEnter.bind(this);
    this.onPromoSlotLeave = this.onPromoSlotLeave.bind(this);
    this.onDesktopMediaChange = this.onDesktopMediaChange.bind(this);
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.preloadedPromoImages = new Set();
    this.promoHideTimeouts = new Map();
    this.promoAnimationFrames = new Map();
    this.promoHideDelay = 250;
    this.videoDebug = new URLSearchParams(window.location.search).get('marijoaVideoDebug') === '1';
  }

  connectedCallback() {
    this.trigger = this.querySelector('.marijoa-mega__trigger');
    this.panel = this.querySelector('.marijoa-mega__panel');
    this.promoSlots = this.querySelectorAll('[data-marijoa-promo-slot]');
    this.promoTriggers = this.querySelectorAll('[data-marijoa-promo-trigger]');
    this.desktopMediaQuery = window.matchMedia('(min-width: 990px)');

    if (!this.trigger || !this.panel) return;

    this.trigger.addEventListener('click', this.onTriggerClick);
    this.promoTriggers.forEach((trigger) => {
      trigger.addEventListener('mouseenter', this.onPromoTrigger);
      trigger.addEventListener('focus', this.onPromoTrigger);
    });
    this.promoSlots.forEach((slot) => {
      slot.addEventListener('mouseenter', this.onPromoSlotEnter);
      slot.addEventListener('mouseleave', this.onPromoSlotLeave);
    });
    this.panel.addEventListener('mouseover', this.onPromoTriggerDelegated);
    this.panel.addEventListener('focusin', this.onPromoTriggerDelegated);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('focusin', this.onDocumentFocusIn);
    document.addEventListener('keydown', this.onKeyDown);
    this.desktopMediaQuery.addEventListener('change', this.onDesktopMediaChange);
    this.logLiquidVideoDebug();
    this.initializeDefaultPromoHover();
    this.close();
  }

  disconnectedCallback() {
    if (this.trigger) {
      this.trigger.removeEventListener('click', this.onTriggerClick);
    }

    this.promoTriggers.forEach((trigger) => {
      trigger.removeEventListener('mouseenter', this.onPromoTrigger);
      trigger.removeEventListener('focus', this.onPromoTrigger);
    });
    this.promoSlots.forEach((slot) => {
      slot.removeEventListener('mouseenter', this.onPromoSlotEnter);
      slot.removeEventListener('mouseleave', this.onPromoSlotLeave);
    });
    if (this.panel) {
      this.panel.removeEventListener('mouseover', this.onPromoTriggerDelegated);
      this.panel.removeEventListener('focusin', this.onPromoTriggerDelegated);
    }
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('focusin', this.onDocumentFocusIn);
    document.removeEventListener('keydown', this.onKeyDown);
    if (this.desktopMediaQuery) {
      this.desktopMediaQuery.removeEventListener('change', this.onDesktopMediaChange);
    }
    this.resetAllPromoSlots();
  }

  onTriggerClick(event) {
    event.preventDefault();
    event.stopPropagation();

    this.isOpen() ? this.close() : this.open();
  }

  onDocumentClick(event) {
    if (!this.isOpen() || this.contains(event.target)) return;

    this.close();
  }

  onDocumentFocusIn(event) {
    if (!this.isOpen() || this.contains(event.target)) return;

    this.close();
  }

  onKeyDown(event) {
    if (!this.isOpen()) return;

    if (event.key === 'Escape') {
      this.close();
      this.trigger.focus();
    }

    if (event.key === 'ArrowDown' && event.target === this.trigger) {
      event.preventDefault();
      this.focusFirstPanelLink();
    }
  }

  onPromoTrigger(event) {
    this.updatePromosFromTrigger(event.currentTarget);
  }

  onPromoTriggerDelegated(event) {
    const trigger = event.target.closest('[data-marijoa-promo-trigger]');

    if (!trigger || !this.contains(trigger)) return;

    this.updatePromosFromTrigger(trigger);
  }

  onPromoSlotEnter(event) {
    if (!this.desktopMediaQuery.matches) return;

    const slot = event.currentTarget;
    const hoverType = slot.dataset.promoHoverType;

    this.cancelPromoHide(slot);
    this.cancelPromoActivation(slot);

    if (hoverType === 'video') {
      const video = slot.querySelector('.marijoa-mega__promo-hover-video');
      if (!video || !video.querySelector('source')) return;

      const wasHidden = video.hidden;
      video.hidden = false;
      if (wasHidden) video.load();
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch((error) => {
          this.debugVideo('video.play() error', {
            title: slot.dataset.promoTitle || '',
            error
          });
        });
      }
      this.schedulePromoActivation(slot);
    } else if (hoverType === 'image') {
      const hoverImage = slot.querySelector('.marijoa-mega__promo-hover-image');
      if (!hoverImage || !hoverImage.src) return;

      hoverImage.hidden = false;
      this.schedulePromoActivation(slot);
    }
  }

  onPromoSlotLeave(event) {
    this.hidePromoSlotWithTransition(event.currentTarget);
  }

  onDesktopMediaChange(event) {
    if (!event.matches) this.resetAllPromoSlots();
  }

  updatePromosFromTrigger(trigger) {
    if (!this.hasPromoData(trigger)) return;

    this.updatePromos(trigger);
  }

  isOpen() {
    return this.hasAttribute('open');
  }

  open() {
    MarijoaMegaMenu.closeAll(this);
    this.setAttribute('open', '');
    this.panel.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.schedulePromoPreload();
  }

  close() {
    this.removeAttribute('open');
    this.resetAllPromoSlots();

    if (this.panel) {
      this.panel.hidden = true;
    }

    if (this.trigger) {
      this.trigger.setAttribute('aria-expanded', 'false');
    }
  }

  focusFirstPanelLink() {
    const firstLink = this.panel.querySelector('a[href], button:not([disabled])');

    if (firstLink) {
      firstLink.focus();
    }
  }

  hasPromoData(trigger) {
    return [1, 2, 3].some((index) => (
      this.getPromoValue(trigger, 'image', index)
      || this.getPromoValue(trigger, 'hover-type', index)
      || this.getPromoValue(trigger, 'hover-image', index)
      || this.getPromoValue(trigger, 'video-sources', index)
    ));
  }

  updatePromos(trigger) {
    this.promoSlots.forEach((slot) => {
      this.resetPromoSlot(slot);

      const index = slot.dataset.marijoaPromoSlot;
      const image = this.getPromoValue(trigger, 'image', index) || slot.dataset.defaultImage;
      const link = this.getPromoValue(trigger, 'url', index) || this.getPromoValue(trigger, 'link', index) || slot.dataset.defaultLink;
      const title = this.getPromoValue(trigger, 'title', index) || slot.dataset.defaultTitle || '';
      const hoverType = this.getPromoValue(trigger, 'hover-type', index) || slot.dataset.defaultHoverType || 'none';
      const hoverImage = this.getPromoValue(trigger, 'hover-image', index) || slot.dataset.defaultHoverImage;
      const videoSources = this.getPromoValue(trigger, 'video-sources', index) || slot.dataset.defaultVideoSources;
      const videoPoster = this.getPromoValue(trigger, 'video-poster', index) || slot.dataset.defaultVideoPoster;
      const imageElement = slot.querySelector('.marijoa-mega__promo-image');
      const placeholder = slot.querySelector('.marijoa-mega__promo-placeholder');

      if (!image) return;

      if (imageElement) {
        imageElement.hidden = false;
        imageElement.src = image;
        imageElement.alt = title;
        imageElement.title = title;
      }
      if (placeholder) placeholder.hidden = true;

      this.debugVideo('updatePromos()', { title, hoverType, videoSources });
      this.configurePromoHover(slot, hoverType, hoverImage, videoSources, videoPoster, title);

      if (title) {
        slot.title = title;
      } else {
        slot.removeAttribute('title');
      }

      if (link) {
        slot.href = link;
      } else {
        slot.removeAttribute('href');
      }

      let titleElement = slot.querySelector('.marijoa-mega__promo-title');
      if (title) {
        if (!titleElement) {
          titleElement = document.createElement('span');
          titleElement.className = 'marijoa-mega__promo-title';
          slot.append(titleElement);
        }
        titleElement.textContent = title;
        titleElement.hidden = false;
      } else if (titleElement) {
        titleElement.hidden = true;
      }

      slot.hidden = false;
    });
  }

  initializeDefaultPromoHover() {
    this.promoSlots.forEach((slot) => {
      this.configurePromoHover(
        slot,
        slot.dataset.defaultHoverType || 'none',
        slot.dataset.defaultHoverImage,
        slot.dataset.defaultVideoSources,
        slot.dataset.defaultVideoPoster,
        slot.dataset.defaultTitle || ''
      );
    });
  }

  configurePromoHover(slot, hoverType, hoverImage, videoSources, videoPoster, title = '') {
    const hoverImageElement = slot.querySelector('.marijoa-mega__promo-hover-image');
    const videoElement = slot.querySelector('.marijoa-mega__promo-hover-video');
    const sources = this.parseVideoSources(videoSources, title);
    let resolvedType = hoverType;

    if (hoverImageElement) {
      hoverImageElement.hidden = true;
      hoverImageElement.removeAttribute('src');
      hoverImageElement.alt = '';
    }

    if (videoElement) {
      this.stopAndResetVideo(videoElement);
      videoElement.replaceChildren();
      videoElement.removeAttribute('poster');
      videoElement.load();
    }

    if (hoverType === 'video' && videoElement) {
      sources.forEach((sourceData) => {
        const sourceUrl = typeof sourceData === 'string' ? sourceData : sourceData?.url;
        if (!sourceUrl) return;

        const source = document.createElement('source');
        source.src = sourceUrl;
        if (typeof sourceData === 'object' && sourceData.mime_type) source.type = sourceData.mime_type;
        videoElement.append(source);
      });

      if (videoPoster) videoElement.poster = videoPoster;
      if (!sources.length) {
        resolvedType = 'none';
      }
    } else if (hoverType === 'image' && hoverImageElement && hoverImage) {
      hoverImageElement.src = hoverImage;
    } else {
      resolvedType = 'none';
    }

    this.debugVideo('sources agregados al video', {
      title,
      sources: videoElement
        ? Array.from(videoElement.querySelectorAll('source')).map((source) => ({
            src: source.src,
            type: source.type
          }))
        : []
    });

    slot.dataset.promoHoverType = resolvedType;
    slot.dataset.promoTitle = title;
  }

  parseVideoSources(value, title = '') {
    if (!value) {
      this.debugVideo('resultado de JSON.parse', {
        title,
        videoSources: value,
        parsedSources: [],
        skipped: true
      });
      return [];
    }

    try {
      const sources = JSON.parse(value);
      const parsedSources = Array.isArray(sources) ? sources : [];
      this.debugVideo('resultado de JSON.parse', { title, videoSources: value, parsedSources });
      return parsedSources;
    } catch (error) {
      this.debugVideo('JSON.parse error', { title, videoSources: value, error });
      return [];
    }
  }

  logLiquidVideoDebug() {
    if (!this.videoDebug) return;

    const debugElement = this.querySelector('[data-marijoa-liquid-video-debug]');
    if (!debugElement) return;

    try {
      this.debugVideo('Liquid hover_video', JSON.parse(debugElement.textContent));
    } catch (error) {
      this.debugVideo('Liquid debug JSON.parse error', {
        value: debugElement.textContent,
        error
      });
    }
  }

  debugVideo(message, data) {
    if (!this.videoDebug) return;
    console.info(`[Marijoa video debug] ${message}`, data);
  }

  stopAndResetVideo(video) {
    if (!video) return;

    video.pause();
    try {
      video.currentTime = 0;
    } catch (error) {
      // The video may not have loaded metadata yet.
    }
    video.hidden = true;
  }

  schedulePromoActivation(slot) {
    const frameIds = [];
    const firstFrame = window.requestAnimationFrame(() => {
      const secondFrame = window.requestAnimationFrame(() => {
        this.promoAnimationFrames.delete(slot);
        slot.classList.add('is-hover-active');
      });

      frameIds.push(secondFrame);
    });

    frameIds.push(firstFrame);
    this.promoAnimationFrames.set(slot, frameIds);
  }

  cancelPromoActivation(slot) {
    const frameIds = this.promoAnimationFrames.get(slot);
    if (!frameIds) return;

    frameIds.forEach((frameId) => window.cancelAnimationFrame(frameId));
    this.promoAnimationFrames.delete(slot);
  }

  cancelPromoHide(slot) {
    const timeoutId = this.promoHideTimeouts.get(slot);
    if (timeoutId === undefined) return;

    window.clearTimeout(timeoutId);
    this.promoHideTimeouts.delete(slot);
  }

  hidePromoSlotWithTransition(slot) {
    if (!slot) return;

    this.cancelPromoActivation(slot);
    this.cancelPromoHide(slot);
    slot.classList.remove('is-hover-active');

    const video = slot.querySelector('.marijoa-mega__promo-hover-video');
    if (video) video.pause();

    const timeoutId = window.setTimeout(() => {
      const hoverImage = slot.querySelector('.marijoa-mega__promo-hover-image');
      if (hoverImage) hoverImage.hidden = true;
      this.stopAndResetVideo(video);
      this.promoHideTimeouts.delete(slot);
    }, this.promoHideDelay);

    this.promoHideTimeouts.set(slot, timeoutId);
  }

  resetPromoSlot(slot) {
    if (!slot) return;

    this.cancelPromoActivation(slot);
    this.cancelPromoHide(slot);
    slot.classList.remove('is-hover-active');
    const hoverImage = slot.querySelector('.marijoa-mega__promo-hover-image');
    if (hoverImage) hoverImage.hidden = true;
    this.stopAndResetVideo(slot.querySelector('.marijoa-mega__promo-hover-video'));
  }

  resetAllPromoSlots() {
    if (!this.promoSlots) return;
    this.promoSlots.forEach((slot) => this.resetPromoSlot(slot));
  }

  getPromoValue(trigger, key, index) {
    const datasetKey = `promo${key.charAt(0).toUpperCase()}${key.slice(1)}${index}`;
    const attributeKey = `data-promo-${key}-${index}`;

    return trigger.dataset[datasetKey] || trigger.getAttribute(attributeKey);
  }

  schedulePromoPreload() {
    if (!this.classList.contains('marijoa-mega--tejidos')) return;

    const preload = () => this.preloadPromoImages();

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(preload);
    } else {
      window.setTimeout(preload, 150);
    }
  }

  preloadPromoImages() {
    this.promoTriggers.forEach((trigger) => {
      [1, 2, 3].forEach((index) => {
        const image = this.getPromoValue(trigger, 'image', index);

        if (!image || this.preloadedPromoImages.has(image)) return;

        this.preloadedPromoImages.add(image);
        const preloader = new Image();
        preloader.src = image;
      });
    });
  }

  static closeAll(except = null) {
    document.querySelectorAll('marijoa-mega-menu[open]').forEach((menu) => {
      if (menu !== except && typeof menu.close === 'function') {
        menu.close();
      }
    });
  }
}

if (!customElements.get('marijoa-mega-menu')) {
  customElements.define('marijoa-mega-menu', MarijoaMegaMenu);
}

const marijoaMobileInlineAccordionSelector = '[data-marijoa-mobile-inline-accordion]';

document.addEventListener('click', (event) => {
  const summary = event.target.closest(`${marijoaMobileInlineAccordionSelector} > summary`);

  if (!summary || !window.matchMedia('(max-width: 989px)').matches) return;

  const accordion = summary.parentElement;
  const accordionGroup = accordion.closest('.marijoa-mobile-menu__group');
  const willOpen = !accordion.open;

  event.preventDefault();
  event.stopImmediatePropagation();

  if (willOpen && accordionGroup) {
    accordionGroup.querySelectorAll(`${marijoaMobileInlineAccordionSelector}[open]`).forEach((openAccordion) => {
      if (openAccordion !== accordion) openAccordion.removeAttribute('open');
    });
  }

  accordion.toggleAttribute('open', willOpen);
}, true);

document.addEventListener('keyup', (event) => {
  if (event.key !== 'Escape' || !window.matchMedia('(max-width: 989px)').matches) return;

  const accordion = event.target.closest(`${marijoaMobileInlineAccordionSelector}[open]`);

  if (!accordion) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  accordion.removeAttribute('open');
  accordion.querySelector('summary').focus();
}, true);
